import { PropsWithChildren, useEffect, useState } from 'react';
import { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { Box, Modal, Fade, Typography, GridLegacy as Grid, Button, Tooltip } from '@mui/material';
import i18n from '../../../libs/language';
import AppGridContainer from '@old/ui/app-grid-container';
import { BaseTable } from '@old/ui/table';
import DeleteIcon from '@old/assets/svg/core/trash-red.svg';
import { configButtonStylesContained } from '@old/styles/generic-styles';
import { useEffectOnUpdate } from '@old/hooks/core';
import { useService } from '@old/hooks/use-service';
import { ToastAction } from '@old/ui/toast/toast-action';
import { useGlobalStore } from '@old/global-store/use-store';
import { listSilobagService } from '@old/services/domain/base/silobag/base/silobag-list';
import { silobagMassiveCloseService } from '@old/services/domain/base/silobag/base/massive-close';
import { listMonitoredSilobagProtocols } from '@old/services/domain/base/silobag/base/silobag-protocol';
import { MonitoredWarningExtractionModal } from '../silobag-detail/exctraction-modal/monitored-warning-extraction-modal';
import VerifiedIcon from '@mui/icons-material/Verified';

type SummarySilobagList = Awaited<ReturnType<typeof listSilobagService>>['summary']['data'];
type SilobagModalMassiveCloseProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  silobagData: SummarySilobagList;
  setIsMassiveClose: (isMassiveClose: boolean) => void;
};

type TitleProps = {
  fullSiloCount: number;
};

type TableSectionProps = {
  fullRows: FullRows;
  onDeleteRow: (id: number) => void;
  monitoredSilobagIds: Set<string>;
};

type InitialViewProps = {
  noPermissionRows: PermissionRows;
  onContinue: () => void;
  onCancel: () => void;
  fullSiloCount: number;
  noPermissionCount: number;
};

type ActionButtonsProps = {
  handleClose: () => void;
};

const commonButtonStyles = {
  fontWeight: 600,
  textTransform: 'none',
  display: 'flex',
  color: '#FF0000',
};

const VIEW_TYPE = {
  INITIAL: 'initial',
  FINAL: 'final',
};

const formatNumberWithThousandsSeparator = (num: number | null | undefined) =>
  num != null && !isNaN(num) ? num.toLocaleString('en-US') : 'N/A';

const formatPercentage = (num: number | null | undefined) =>
  num != null && !isNaN(num) ? num.toFixed(2) : 'N/A';

const renderCellBold = (params: GridCellParams) => (
  <div style={{ textAlign: 'center', fontWeight: 'bold' }}>{params.value as React.ReactNode}</div>
);

const renderIdCell = (monitoredSilobagIds: Set<string>) => (params: GridCellParams) => {
  const silobagId = params.value as string;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 6,
        fontWeight: 'bold',
        width: '100%',
      }}
    >
      <span>{silobagId}</span>
      {monitoredSilobagIds.has(silobagId) ? (
        <Tooltip title={i18n.t('app.siloreal_with_vrr')}>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {/* <img src={VerifiedIcon} alt="monitored" style={{ width: 16, height: 16 }} /> */}
            <VerifiedIcon sx={{ color: '#68BD00', fontSize: 16 }} />
          </span>
        </Tooltip>
      ) : null}
    </div>
  );
};

// Columnas de la tabla
const columns = (
  onDeleteRow: (id: number) => void,
  excludeActions = false,
  monitoredSilobagIds: Set<string> = new Set()
): GridColDef[] => {
  const baseColumns: GridColDef[] = [
    {
      field: 'id',
      display: 'flex',
      headerName: 'ID',
      width: excludeActions ? 400 : 100,
      renderCell: excludeActions ? renderCellBold : renderIdCell(monitoredSilobagIds),
      headerAlign: excludeActions ? 'center' : 'left',
      align: excludeActions ? 'center' : 'left',
    },
    {
      field: 'name',
      display: 'flex',
      headerName: i18n.t('app.name'),
      flex: 1,
      width: excludeActions ? 400 : 100,
      headerAlign: excludeActions ? 'center' : 'left',
      align: excludeActions ? 'center' : 'left',
    },
  ];

  if (!excludeActions) {
    baseColumns.push(
      {
        field: 'weight_bagging',
        display: 'flex',
        headerName: i18n.t('app.silobag_data.weight_bagging'),
        flex: 1,
        width: 200,
        renderCell: (params: GridCellParams) =>
          `${formatNumberWithThousandsSeparator(params.value as number)} kg`,
      },
      {
        field: 'weight_extractions',
        display: 'flex',
        headerName: i18n.t('app.silobag_detail.weight_extractions'),
        flex: 1,
        width: 220,
        renderCell: (params: GridCellParams) =>
          `${formatNumberWithThousandsSeparator(params.value as number)} kg`,
      },
      {
        field: 'difference',
        display: 'flex',
        headerName: i18n.t('app.silobag_detail.difference'),
        flex: 1,
        width: 200,
        renderCell: (params: GridCellParams) =>
          `${formatNumberWithThousandsSeparator(params.value as number)} kg`,
      },
      {
        field: 'percentage',
        display: 'flex',
        headerName: i18n.t('app.silobag_detail.difference_percentage'),
        flex: 1,
        width: 100,
        renderCell: (params: GridCellParams) => `${formatPercentage(params.value as number)}%`,
      },
      {
        field: 'actions',
        display: 'flex',
        headerName: 'Acciones',
        width: 150,
        renderCell: (params: GridCellParams) => (
          <Button
            sx={commonButtonStyles}
            startIcon={<img src={DeleteIcon} alt="delete-icon" />}
            onClick={() => onDeleteRow(params.id as number)}
          ></Button>
        ),
      }
    );
  }

  return baseColumns;
};
// Sección de la tabla
const TableSection = ({ fullRows, onDeleteRow, monitoredSilobagIds }: TableSectionProps) => (
  <Grid item xs={12}>
    <Grid item xs={12} md={12}>
      <BaseTable
        rows={fullRows}
        columns={columns(onDeleteRow, false, monitoredSilobagIds)}
        initialPageSize={16}
      />
    </Grid>
  </Grid>
);

// Título de la sección
const Title = ({ fullSiloCount }: TitleProps) => (
  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
    {i18n.t('app.silobag_close_modal.silobags')} ({fullSiloCount})
  </Typography>
);

// Descripción
const DescriptionSection = () => (
  <Grid item xs={12} textAlign="center">
    <Typography variant="body1">
      <strong>{i18n.t('app.silobag_close_modal.question')}</strong>
    </Typography>
    <Typography variant="body2">{i18n.t('app.silobag_close_modal.description')}</Typography>
  </Grid>
);

// Botones de acción  (Cancelar y Confirmar)
const ActionButtons = ({
  handleClose,
  fullRows,
  onConfirm,
}: ActionButtonsProps & {
  fullRows: { id: string }[];
  onConfirm: (ids: { id: string }[]) => void;
}) => {
  const handleConfirmation = () => {
    onConfirm(fullRows.map((row) => ({ id: row.id })));
  };

  return (
    <Box display={'flex'} justifyContent={'end'}>
      <Button onClick={handleClose} sx={{ color: 'black' }}>
        {i18n.t('app.cancel')}
      </Button>
      <Button
        variant="contained"
        size="small"
        sx={{ ...configButtonStylesContained, background: '#FF0000', color: '#FFFFFF' }}
        disabled={fullRows.length === 0}
        onClick={handleConfirmation}
      >
        {i18n.t('app.silobag_close_modal.confirmation')}
      </Button>
    </Box>
  );
};

// Vista inicial
const InitialView = ({
  noPermissionRows,
  onContinue,
  onCancel,
  noPermissionCount,
  fullSiloCount,
}: InitialViewProps) => (
  <Box>
    <Title fullSiloCount={noPermissionCount} />
    <Typography variant="subtitle1" sx={{ textAlign: 'center', marginBottom: 2 }}>
      {i18n.t('app.silobag_close_modal.permissions')}
    </Typography>
    <BaseTable rows={noPermissionRows} columns={columns(() => {}, true)} initialPageSize={16} />
    <Box display={'flex'} justifyContent={'end'} mt={2}>
      <Button onClick={onCancel} size="small" sx={{ color: 'black' }}>
        {i18n.t('app.cancel')}
      </Button>
      <Button
        variant="contained"
        size="small"
        sx={{ ...configButtonStylesContained }}
        disabled={fullSiloCount === 0}
        onClick={onContinue}
      >
        {i18n.t('app.continue')}
      </Button>
    </Box>
  </Box>
);

type FullRows = {
  id: string;
  name: string;
  weight_bagging: number;
  weight_extractions: number;
  difference: number;
  percentage: number;
  silobag_data_statuses: { id: number | undefined };
}[];

type PermissionRows = {
  id: string;
  name: string;
  weight_bagging: number;
  weight_extractions: number;
  difference: number;
  percentage: number;
}[];

export function SilobagModalMassiveClose({
  open,
  setOpen,
  silobagData,
  setIsMassiveClose,
}: SilobagModalMassiveCloseProps) {
  const [view, setView] = useState(VIEW_TYPE.INITIAL);
  const [fullRows, setFullRows] = useState<FullRows>([]);
  const [showToast, setShowToast] = useState(false);
  const [monitoredSilobagIds, setMonitoredSilobagIds] = useState<Set<string>>(new Set());
  const [openMonitoredWarningModal, setOpenMonitoredWarningModal] = useState(false);
  const [monitoredSilobagsToAlert, setMonitoredSilobagsToAlert] = useState<string[]>([]);
  const [pendingCloseIds, setPendingCloseIds] = useState<{ id: string }[] | null>(null);
  const { filters, setFilters } = useGlobalStore();

  // Filas de la tabla con silobags sin permisos
  const noPermissionRows: PermissionRows =
    silobagData
      ?.filter(({ read_permission, write_permission }) => read_permission && !write_permission)
      .map(({ id, name, input_weight, total_extracted_weight }) => {
        const weightBagging = parseFloat(input_weight);
        const weightExtractions = parseFloat(total_extracted_weight);
        const difference = weightBagging - weightExtractions;
        const percentage = (difference / weightBagging) * 100;

        return {
          id,
          name,
          weight_bagging: weightBagging,
          weight_extractions: weightExtractions,
          difference,
          percentage,
        };
      }) || [];

  const noPermissionCount = noPermissionRows?.length;

  // Filas de la tabla con silobags con permisos
  const fullRowsData: FullRows =
    silobagData
      ?.filter(({ read_permission, write_permission }) => read_permission && write_permission)
      .map(({ id, name, input_weight, total_extracted_weight }) => {
        const weightBagging = parseFloat(input_weight);
        const weightExtractions = parseFloat(total_extracted_weight);
        const difference = weightBagging - weightExtractions;
        const percentage = (difference / weightBagging) * 100;
        return {
          id,
          name,
          weight_bagging: weightBagging,
          weight_extractions: weightExtractions,
          difference,
          percentage,
          silobag_data_statuses: { id: undefined },
        };
      }) || [];

  const fullSiloCount = fullRowsData?.length;
  const hasCompleteStatus = fullRowsData.some((silo) => silo.silobag_data_statuses?.id === 2);

  useEffectOnUpdate(() => {
    setFullRows(fullRowsData);
  }, [silobagData]);

  useEffect(() => {
    if (!open || fullRows.length === 0) {
      setMonitoredSilobagIds(new Set());
      return;
    }

    let cancelled = false;

    const fetchMonitoredSilobags = async () => {
      try {
        const { summary } = await listMonitoredSilobagProtocols({
          query: { ids: fullRows.map((row) => row.id) },
        });

        if (cancelled) return;

        setMonitoredSilobagIds(
          new Set(summary?.filter((item) => item.protocol != null).map((item) => item.silobag_id) ?? [])
        );
      } catch {
        if (!cancelled) {
          setMonitoredSilobagIds(new Set());
        }
      }
    };

    void fetchMonitoredSilobags();

    return () => {
      cancelled = true;
    };
  }, [open, fullRows]);

  // Servicio para cerrar silobags
  const { trigger: createMassiveCloseSilobagService, isSuccess: isMassiveClose } = useService(
    silobagMassiveCloseService,
    {
      onSuccess: () => {
        setShowToast(true);
        if (hasCompleteStatus) {
          setFilters({ ...filters, silobag_data_statuses: 1 });
        }
        setFullRows([]);
      },
    }
  );

  const handleMassiveClose = (ids: { id: string }[], acceptProtocolInvalidation = false) => {
    createMassiveCloseSilobagService({
      body: ids,
      accept_protocol_invalidation: acceptProtocolInvalidation,
    });
  };

  const handleConfirmClose = (ids: { id: string }[]) => {
    const hasMonitoredSilobags = ids.some(({ id }) => monitoredSilobagIds.has(id));

    if (hasMonitoredSilobags) {
      const monitoredIds = ids.filter(({ id }) => monitoredSilobagIds.has(id)).map(({ id }) => id);
      setMonitoredSilobagsToAlert(monitoredIds);
      setPendingCloseIds(ids);
      setOpenMonitoredWarningModal(true);
      return;
    }

    handleMassiveClose(ids, false);
    handleClose();
  };

  const handleConfirmMonitoredClose = () => {
    setOpenMonitoredWarningModal(false);

    if (!pendingCloseIds) return;

    handleMassiveClose(pendingCloseIds, true);
    setPendingCloseIds(null);
    handleClose();
  };

  // Cierra el modal
  const handleClose = () => {
    setView(VIEW_TYPE.INITIAL);
    setOpen(false);
  };

  // Cambia la vista a la final
  const handleContinue = () => {
    setView(VIEW_TYPE.FINAL);
  };

  // Elimina la fila de la tabla
  const handleDeleteRow = (id: number | string) => {
    setFullRows((prevRows) => prevRows.filter((row) => row.id !== id));
  };

  useEffectOnUpdate(() => {
    if (isMassiveClose) {
      setIsMassiveClose(true);
    }
  }, [isMassiveClose]);

  useEffectOnUpdate(() => {
    if (!open) {
      setView(VIEW_TYPE.INITIAL);
      setShowToast(false);
      setOpenMonitoredWarningModal(false);
      setPendingCloseIds(null);
      setMonitoredSilobagsToAlert([]);
      setMonitoredSilobagIds(new Set());
    }
  }, [open]);

  return (
    <>
      {openMonitoredWarningModal ? (
        <MonitoredWarningExtractionModal
          open={openMonitoredWarningModal}
          setOpen={setOpenMonitoredWarningModal}
          handleSave={handleConfirmMonitoredClose}
          monitoredSilobagIds={monitoredSilobagsToAlert}
        />
      ) : null}
      <MyModal onClose={handleClose} open={open}>
        {view === VIEW_TYPE.INITIAL && noPermissionRows.length > 0 ? (
          <InitialView
            noPermissionRows={noPermissionRows}
            onContinue={handleContinue}
            onCancel={handleClose}
            fullSiloCount={fullSiloCount}
            noPermissionCount={noPermissionCount}
          />
        ) : (
          <>
            <Title fullSiloCount={fullSiloCount} />
            <AppGridContainer spacing={5}>
              <TableSection
                fullRows={fullRows}
                onDeleteRow={handleDeleteRow}
                monitoredSilobagIds={monitoredSilobagIds}
              />
              <DescriptionSection />
            </AppGridContainer>
            <ActionButtons
              handleClose={() => setOpen(false)}
              fullRows={fullRows}
              onConfirm={handleConfirmClose}
            />
          </>
        )}
      </MyModal>
      {showToast ? (
        <ToastAction message={i18n.t('app.silobag_close_modal.silobags_closed')} severity="success" />
      ) : null}
    </>
  );
}

type ModalProps = {
  open: boolean;
  onClose: () => void;
};

function MyModal({ open, onClose, children }: PropsWithChildren<ModalProps>) {
  if (!open) return null;
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    overflowX: 'hidden',
    maxHeight: '95vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  };

  const handleClose = (_: never, reason: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };
  return (
    <Modal open={open} keepMounted onClose={handleClose}>
      <Fade in={open} appear={false} timeout={175}>
        <Box sx={style}>{children}</Box>
      </Fade>
    </Modal>
  );
}
